"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Button = void 0;
var react_1 = __importDefault(require("react"));
var classnames_1 = __importDefault(require("classnames"));
var __1 = require("../");
var utilities_1 = require("../../utilities");
var hooks_1 = require("../../hooks");
var defaultProps = {
    el: 'button',
};
/**
 * **Buttons** provide a possible user actions
 */
exports.Button = react_1.default.forwardRef(function (_a, ref) {
    var children = _a.children, className = _a.className, _b = _a.color, color = _b === void 0 ? 'default' : _b, content = _a.content, _c = _a.el, Element = _c === void 0 ? defaultProps.el : _c, _d = _a.fill, fill = _d === void 0 ? 'solid' : _d, full = _a.full, href = _a.href, icon = _a.icon, iconName = _a.iconName, _e = _a.iconPosition, iconPosition = _e === void 0 ? 'left' : _e, inactive = _a.inactive, large = _a.large, loading = _a.loading, negative = _a.negative, outline = _a.outline, primary = _a.primary, selected = _a.selected, _f = _a.size, size = _f === void 0 ? 'medium' : _f, small = _a.small, text = _a.text, type = _a.type, _g = _a.width, width = _g === void 0 ? 'auto' : _g, xsmall = _a.xsmall, onFocus = _a.onFocus, onBlur = _a.onBlur, props = __rest(_a, ["children", "className", "color", "content", "el", "fill", "full", "href", "icon", "iconName", "iconPosition", "inactive", "large", "loading", "negative", "outline", "primary", "selected", "size", "small", "text", "type", "width", "xsmall", "onFocus", "onBlur"]);
    var newRef = react_1.default.useRef(null);
    var itemRef = ref ? ref : newRef;
    var _h = hooks_1.useFocusVisible(itemRef, onBlur, onFocus), isFocusVisible = _h.isFocusVisible, focusFunction = _h.focusFunction, blurFunction = _h.blurFunction;
    var getTagName = function () {
        // If `el` is passed in and isn't default, it is always used
        if (Element !== defaultProps.el)
            return Element;
        // If `href` is passed in, the element becomes an anchor
        if (href)
            return 'a';
        // Is default, use it
        return Element;
    };
    var getTabIndex = function () {
        if (props.tabIndex)
            return props.tabIndex;
        if (getTagName() !== 'button')
            0;
        return null;
    };
    var getColorClass = function () {
        var realColor = color;
        // Override string props with boolean values
        if (primary)
            realColor = 'primary';
        if (negative)
            realColor = 'negative';
        var ButtonColorClasses = {
            primary: 'Button--blue',
            blue: 'Button--blue',
            default: 'Button--grey',
            grey: 'Button--grey',
            negative: 'Button--red',
            red: 'Button--red',
        };
        return ButtonColorClasses[realColor];
    };
    var getFillClass = function () {
        var realFill = fill;
        if (outline)
            realFill = 'outline';
        if (text || fill === 'subtle' || fill === 'none')
            realFill = 'subtle';
        if (realFill === 'solid')
            return 'Button--solid';
        if (realFill === 'outline')
            return 'Button--outline';
        if (realFill === 'subtle')
            return 'Button--subtle';
    };
    var getSizeClass = function () {
        var realSize = size;
        if (xsmall)
            realSize = 'xsmall';
        if (small)
            realSize = 'small';
        if (large)
            realSize = 'large';
        if (realSize === 'xsmall')
            return 'Button--xsmall';
        if (realSize === 'small')
            return 'Button--small';
        if (realSize === 'large')
            return 'Button--large';
    };
    var getWidth = function () {
        if (full)
            return 'full';
        return width;
    };
    var getWidthValue = function () {
        var width = getWidth();
        return width !== 'auto' && width !== 'full' ? width : false;
    };
    var getWidthClass = function () {
        return getWidth() === 'full' ? 'Button--full' : null;
    };
    var getTypeAttribute = function () {
        if (type)
            return { type: props.type };
        // Make sure <button type="button" /> is rendered
        if (getTagName() === 'button')
            return { type: 'button' };
    };
    var getHref = function () {
        if (!href)
            return;
        return {
            href: href,
        };
    };
    react_1.default.useEffect(function () {
        if (text)
            console.warn("@servicetitan/design-system: The `text` prop of the Button component has been deprecated. Please use `fill='subtle'` instead.");
        if (fill === 'none')
            console.warn("@servicetitan/design-system: The `fill='none'` prop of the Button component has been deprecated. Please use `fill='subtle'` instead.");
        if (getTagName() !== 'button' && !!getTypeAttribute())
            throw new Error('@servicetitan/design-system: The Button component is using incompatible props. Only button elements can have type attributes.');
        if (getTagName() !== 'a' && href)
            throw new Error('@servicetitan/design-system: The Button component is using incompatible props. Only anchor elements can have href attributes.');
    });
    var hasContent = function () {
        if (children != null)
            return true;
        if (content != null)
            return true;
        return false;
    };
    var hasIcon = function () {
        if (iconName || icon)
            return true;
        return false;
    };
    var renderIcon = function () {
        if (!hasIcon())
            return;
        var ButtonIcon = null;
        if (iconName)
            ButtonIcon = (react_1.default.createElement(__1.Icon, { className: "Button__icon-svg", name: iconName }));
        if (icon)
            ButtonIcon = icon;
        var ButtonIconClasses = classnames_1.default('Button__icon', {
            'Button__icon--left': hasContent() && iconPosition === 'left',
            'Button__icon--right': hasContent() && iconPosition === 'right',
        });
        return react_1.default.createElement("span", { className: ButtonIconClasses }, ButtonIcon);
    };
    var preventClicks = props.disabled || inactive || loading;
    var ButtonClasses = classnames_1.default('Button', className, getColorClass(), getFillClass(), getSizeClass(), getWidthClass(), {
        'Button--disabled': props.disabled,
        'Button--inactive': preventClicks,
        'Button--loading': loading,
        'Button--icon-only': !hasContent(),
        'Button--focus-visible': isFocusVisible,
        'Button--selected': selected,
    });
    var ButtonElement = getTagName();
    return (react_1.default.createElement(ButtonElement, __assign({ className: ButtonClasses, disabled: preventClicks, role: "button", style: { width: getWidthValue() }, tabIndex: getTabIndex(), onFocus: focusFunction, onBlur: blurFunction, ref: itemRef }, getTypeAttribute(), getHref(), props),
        react_1.default.createElement("span", { className: "Button__content" },
            iconPosition === 'left' && renderIcon(),
            utilities_1.getContent({ children: children, content: content }),
            iconPosition === 'right' && renderIcon())));
});
//# sourceMappingURL=Button.js.map